import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { adminApiService } from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import type { AdminCounts } from '../../types'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'
import AdminTabs from './AdminTabs'

/** Each card opens the list behind its number. `alert` highlights a count that needs action when non-zero. */
const CARDS: { key: keyof AdminCounts; label: string; to: string; alert?: boolean }[] = [
  { key: 'teams', label: 'Teams', to: '/app/admin/teams' },
  { key: 'users', label: 'Users', to: '/app/admin/users' },
  { key: 'superAdmins', label: 'Super admins', to: '/app/admin/users?role=super-admin' },
  { key: 'teamAdmins', label: 'Team admins', to: '/app/admin/users?role=team-admin' },
  { key: 'lockedUsers', label: 'Locked users', to: '/app/admin/users?status=locked', alert: true },
  { key: 'invitedUsers', label: 'Pending invites', to: '/app/admin/users?status=invited' },
  { key: 'complianceTasksDue', label: 'Compliance tasks due', to: '/app/admin/compliance', alert: true },
  { key: 'openAuditorRequests', label: 'Open auditor requests', to: '/app/admin/auditors', alert: true },
]

export default function AdminDashboardPage() {
  const [data, setData] = useState<AdminCounts | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    adminApiService.getDashboard()
      .then(setData)
      .catch(err => setError(err instanceof ApiError ? err.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <AdminTabs />

      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, marginBottom: spacing.xl }}>
        Admin Dashboard
      </h1>

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : data ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))', gap: spacing.md }}>
          {CARDS.map(c => {
            const value = data[c.key] ?? 0
            const hot = c.alert && value > 0
            return (
              <Link key={c.key} to={c.to} style={{
                display: 'block', textDecoration: 'none', minWidth: 0,
                background: colors.surface,
                border: `1px solid ${hot ? colors.error : colors.border}`,
                borderRadius: radius.md,
                padding: spacing.lg,
                boxShadow: shadow.sm,
              }}>
                <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>{c.label}</div>
                <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: hot ? colors.error : colors.text }}>{value}</div>
              </Link>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
